import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import { getAccounts, getTransactionsCredencial, getBalance, getCVUInfo } from '../services/accountService'
import { executeTransfer, executeBulkTransfer, generateOriginId, getTransferInfoCoelsa, getTransferInfoC2C, getBatchStatus } from '../services/transferService'
import { listCVUs } from '../services/cvuService'
import { verifyPassword } from '../services/authService'
import { LuArrowLeftRight, LuTrash2, LuDownload, LuUpload, LuFile, LuCircleCheck, LuPlay, LuSearch, LuEye, LuX, LuHistory, LuLoader } from 'react-icons/lu'
import PasswordModal from '../components/PasswordModal'
import TransferSuccessModal from '../components/TransferSuccessModal'
import './TransferPage.css'

// Función para formatear monto con puntos de miles (formato argentino)
const formatMontoInput = (value) => {
  // Quitar todo excepto números y coma
  let cleaned = value.replace(/[^\d,]/g, '')
  
  // Separar parte entera y decimal
  const parts = cleaned.split(',')
  let integerPart = parts[0] || ''
  let decimalPart = parts[1] !== undefined ? parts[1].slice(0, 2) : ''
  
  // Quitar ceros a la izquierda excepto si es solo "0"
  integerPart = integerPart.replace(/^0+/, '') || ''
  
  // Agregar puntos de miles
  if (integerPart.length > 3) {
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }
  
  // Reconstruir el valor
  if (parts.length > 1) {
    return integerPart + ',' + decimalPart
  }
  return integerPart
}

// Función para limpiar formato y obtener número
const cleanMontoFormat = (value) => {
  if (!value) return 0
  // Quitar puntos de miles y reemplazar coma por punto
  const cleaned = value.replace(/\./g, '').replace(',', '.')
  return parseFloat(cleaned) || 0
}

function TransferPage() {
  const { userRole, roleReady, isPortalUser, assignedAccounts, portalRole } = useAuth()
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const fileInputRef = useRef(null)

  // Modo de transferencia CREDENCIAL: 'select' | 'unitaria' | 'masiva'
  const [transferMode, setTransferMode] = useState('select')

  // CSV handling for CREDENCIAL (masivas)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [parsedTransfers, setParsedTransfers] = useState([])
  const [showPreview, setShowPreview] = useState(false)

  // Campos para transferencia unitaria CREDENCIAL
  const [unitariaCvuOrigen, setUnitariaCvuOrigen] = useState('')
  const [unitariaAccountNumber, setUnitariaAccountNumber] = useState('') // Para operadores que ingresan manualmente
  const [unitariaMonto, setUnitariaMonto] = useState('')
  const [unitariaCvuDestino, setUnitariaCvuDestino] = useState('')
  const [unitariaDescripcion, setUnitariaDescripcion] = useState('Pago de Servicios')
  
  // Balance de la cuenta seleccionada (unitaria)
  const [unitariaBalance, setUnitariaBalance] = useState(null)
  const [unitariaBalanceLoading, setUnitariaBalanceLoading] = useState(false)
  
  // Info del CVU destino (unitaria)
  const [cvuDestinoInfo, setCvuDestinoInfo] = useState(null)
  const [cvuDestinoLoading, setCvuDestinoLoading] = useState(false)

  // Alias para buscar CVU destino (unitaria)
  const [aliasInput, setAliasInput] = useState('')
  const [aliasVerifying, setAliasVerifying] = useState(false)
  const [aliasError, setAliasError] = useState('')
  const [aliasVerified, setAliasVerified] = useState(false)

  // Modal de confirmación de contraseña
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [verifyingPassword, setVerifyingPassword] = useState(false)
  const [pendingTransferType, setPendingTransferType] = useState(null) // 'unitaria' | 'masiva'
  
  // Modal de éxito transferencia unitaria CREDENCIAL
  const [showUnitariaSuccessModal, setShowUnitariaSuccessModal] = useState(false)
  const [unitariaSuccessData, setUnitariaSuccessData] = useState(null)
  
  // Modal de transferencia FALLIDA
  const [showTransferFailedModal, setShowTransferFailedModal] = useState(false)
  const [transferFailedData, setTransferFailedData] = useState(null)
  

  // Form fields - BIND (mantener para compatibilidad)
  const [originAccount, setOriginAccount] = useState('')
  const [destinationType, setDestinationType] = useState('alias')
  const [destinationValue, setDestinationValue] = useState('')
  const [destinationCbu, setDestinationCbu] = useState('')
  const [destinationCuit, setDestinationCuit] = useState('')
  const [amount, setAmount] = useState('')
  const [concept, setConcept] = useState('VAR')
  const [description, setDescription] = useState('Transferencia')
  const [email, setEmail] = useState('')

  // CVUs dados de alta (para BIND)
  const [registeredCVUs, setRegisteredCVUs] = useState([])

  // Modal de éxito
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successTransferData, setSuccessTransferData] = useState(null)

  // CREDENCIAL - Consultar estado de transferencias
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyTransfers, setHistoryTransfers] = useState([])
  const [selectedTransfer, setSelectedTransfer] = useState(null)
  const [transferDetail, setTransferDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [historyAccount, setHistoryAccount] = useState('')
  const [showAccountInput, setShowAccountInput] = useState(false)
  const [expandedLote, setExpandedLote] = useState(null)
  const [showDevModal, setShowDevModal] = useState(false)
  
  // Constante de comisión COELSA
  const COMISION_COELSA = 0.38

  useEffect(() => {
    if (userRole !== 'CREDENCIAL') {
      if (isPortalUser && assignedAccounts && assignedAccounts.length > 0) {
        const mapped = assignedAccounts.map(acc => ({
          id: acc.account_id || acc.cvu,
          number: acc.cvu,
          type: '',
          balance: { amount: 0 },
          account_routing: { address: acc.cvu }
        }))
        setAccounts(mapped)
        if (mapped.length > 0) setOriginAccount(mapped[0].id)
      } else {
        loadAccounts()
        loadRegisteredCVUs()
      }
    }
  }, [userRole, assignedAccounts])

  const loadRegisteredCVUs = async () => {
    try {
      const response = await listCVUs()
      setRegisteredCVUs(response.data || [])
    } catch (err) {
      console.log('No se pudieron cargar CVUs registrados:', err.message)
    }
  }

  // Cargar balance cuando se selecciona un CVU de origen (unitaria)
  const handleSelectCvuOrigen = async (cvu) => {
    setUnitariaCvuOrigen(cvu)
    setUnitariaBalance(null)
    
    if (!cvu) return
    
    // Buscar el número de cuenta correspondiente al CVU seleccionado
    const selectedAccount = assignedAccounts?.find(acc => acc.cvu === cvu)
    if (!selectedAccount?.account_number) return
    
    setUnitariaBalanceLoading(true)
    try {
      const response = await getBalance(selectedAccount.account_number)
      if (response?.data?.available_for_purchases !== undefined) {
        setUnitariaBalance(response.data.available_for_purchases)
      }
    } catch (err) {
      console.error('Error al cargar balance:', err)
      setUnitariaBalance(null)
    } finally {
      setUnitariaBalanceLoading(false)
    }
  }

  // Consultar info del CVU destino cuando tiene 22 dígitos
  const handleCvuDestinoChange = async (value) => {
    const cleanValue = value.replace(/[^0-9]/g, '')
    setUnitariaCvuDestino(cleanValue)
    setCvuDestinoInfo(null)
    // Si el usuario escribe directamente el CVU, resetear alias verificado
    if (aliasVerified && cleanValue !== unitariaCvuDestino) {
      setAliasVerified(false)
    }
    
    if (cleanValue.length === 22) {
      setCvuDestinoLoading(true)
      try {
        const response = await getCVUInfo(cleanValue)
        if (response?.success && response?.fullResponse) {
          setCvuDestinoInfo(response.fullResponse)
        }
      } catch (err) {
        console.error('Error al consultar CVU destino:', err)
        setCvuDestinoInfo(null)
      } finally {
        setCvuDestinoLoading(false)
      }
    }
  }

  // Verificar alias y obtener CVU asociado
  const handleVerifyAlias = async () => {
    if (!aliasInput.trim()) {
      setAliasError('Ingresá un alias para verificar')
      return
    }
    
    setAliasVerifying(true)
    setAliasError('')
    setAliasVerified(false)
    
    try {
      const response = await getCVUInfo(aliasInput.trim())
      
      if (response?.success && response?.fullResponse) {
        const data = response.fullResponse
        // Si tiene CVU, lo escribimos en el campo de destino
        if (data.cvu) {
          setUnitariaCvuDestino(data.cvu)
          setCvuDestinoInfo(data)
          setAliasVerified(true)
          setAliasError('')
        } else {
          setAliasError('El alias no tiene un CVU asociado')
        }
      } else {
        setAliasError('No se encontró información para este alias')
      }
    } catch (err) {
      console.error('Error al verificar alias:', err)
      const errorMsg = err.response?.data?.error || 'No se pudo verificar el alias'
      setAliasError(errorMsg)
    } finally {
      setAliasVerifying(false)
    }
  }

  const loadAccounts = async () => {
    try {
      const response = await getAccounts()
      const accountsList = response.data || []
      setAccounts(accountsList)
      if (accountsList.length > 0) {
        setOriginAccount(accountsList[0].id)
      }
    } catch (err) {
      setError('Error al cargar cuentas')
    }
  }

  // Descargar plantilla CSV
  const handleDownloadTemplate = () => {
    const headers = ['CVU_ORIGEN', 'MONTO', 'CVU_DESTINO', 'CONCEPTO', 'DESCRIPCION']
    const exampleRow = ['0000003100000000000001', '1500.50', '0000003100000000000002', 'VAR', 'Pago de servicios']
    
    const csvContent = [
      headers.join(','),
      exampleRow.join(','),
      // Fila vacía de ejemplo
      ',,,,'
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'plantilla_transferencias.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Cargar archivo CSV
  const handleFileUpload = (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Limpiar mensajes anteriores
    setError('')
    setSuccess('')

    if (!file.name.endsWith('.csv')) {
      setError('Por favor, cargue un archivo CSV')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target.result
        const lines = text.split('\n').filter(line => line.trim())
        
        // Saltar header
        const dataLines = lines.slice(1)
        
        const transfers = []
        let hasErrors = false
        const invalidAmountLines = []
        const unassignedCvus = new Set() // Para recolectar CVU no asignados únicos

        dataLines.forEach((line, index) => {
          const cols = line.split(',').map(col => col.trim())
          
          // Validar que tenga datos
          if (cols[0] && cols[1] && cols[2]) {
            const transfer = {
              from_cvu: cols[0],
              amount: parseFloat(cols[1]),
              to_cvu: cols[2],
              concept: cols[3] || 'VAR',
              description: cols[4] || 'Transferencia'
            }

            // Validar monto
            if (isNaN(transfer.amount) || transfer.amount <= 0) {
              invalidAmountLines.push(index + 2)
              hasErrors = true
              return
            }

            // Validar que el CVU origen esté asignado al usuario del portal
            if (isPortalUser && assignedAccounts && assignedAccounts.length > 0) {
              const isAssignedCvu = assignedAccounts.some(acc => acc.cvu === transfer.from_cvu)
              if (!isAssignedCvu) {
                unassignedCvus.add(transfer.from_cvu)
                hasErrors = true
                return
              }
            }

            transfers.push(transfer)
          }
        })

        // Mostrar error de montos inválidos
        if (invalidAmountLines.length > 0) {
          setError(`Error en línea(s) ${invalidAmountLines.join(', ')}: Monto inválido`)
          setUploadedFile(null)
          setParsedTransfers([])
          return
        }

        // Mostrar error de CVU no asignados
        if (unassignedCvus.size > 0) {
          const cvuList = Array.from(unassignedCvus)
          if (cvuList.length === 1) {
            setError(`El siguiente CVU no lo tenés asignado a tu cuenta: ${cvuList[0]}`)
          } else {
            setError(`Los siguientes CVU no los tenés asignados a tu cuenta: ${cvuList.join(', ')}`)
          }
          setUploadedFile(null)
          setParsedTransfers([])
          return
        }

        if (hasErrors) {
          setUploadedFile(null)
          setParsedTransfers([])
          return
        }

        if (transfers.length === 0) {
          setError('El archivo no contiene transferencias válidas')
          return
        }

        setError('')
        setUploadedFile(file)
        setParsedTransfers(transfers)
        setShowPreview(true)

      } catch (err) {
        setError('Error al procesar el archivo CSV')
        console.error(err)
      }
    }

    reader.readAsText(file)
  }

  // Iniciar proceso de ejecución MASIVA (primero pide contraseña)
  const handleExecuteBulkTransfer = () => {
    // Si ya está en proceso, ignorar
    if (loading) return
    
    if (parsedTransfers.length === 0) {
      setError('No hay transferencias para ejecutar')
      return
    }
    
    // BLOQUEAR BOTÓN DESDE EL PRIMER CLIC
    setLoading(true)
    setLoadingMessage('Esperando confirmación...')
    setError('')
    setSuccess('')
    setPendingTransferType('masiva')
    setShowPasswordModal(true)
  }

  // Iniciar proceso de ejecución UNITARIA (primero pide contraseña)
  const handleExecuteUnitariaTransfer = () => {
    // Si ya está en proceso, ignorar
    if (loading) return
    
    // Validaciones
    if (!unitariaCvuOrigen) {
      setError('Seleccioná un CVU de origen')
      return
    }
    if (!unitariaMonto || cleanMontoFormat(unitariaMonto) <= 0) {
      setError('Ingresá un monto válido')
      return
    }
    if (!unitariaCvuDestino || unitariaCvuDestino.length !== 22) {
      setError('El CVU destino debe tener 22 dígitos')
      return
    }
    
    // BLOQUEAR BOTÓN DESDE EL PRIMER CLIC
    setLoading(true)
    setLoadingMessage('Esperando confirmación...')
    setError('')
    setSuccess('')
    setPendingTransferType('unitaria')
    setShowPasswordModal(true)
  }

  // Validar contraseña y ejecutar transferencias
  const handlePasswordConfirm = async (password) => {
    setVerifyingPassword(true)
    
    try {
      // Verificar contraseña
      await verifyPassword(password)
      setShowPasswordModal(false)
      
      // Contraseña correcta, ejecutar según el tipo pendiente
      if (pendingTransferType === 'masiva') {
        await executeTransfersAfterValidation()
      } else if (pendingTransferType === 'unitaria') {
        await executeUnitariaTransfer()
      }
      
      return { success: true }
    } catch (err) {
      // Contraseña incorrecta - NO desbloquear el botón aún, el usuario puede reintentar
      return { 
        success: false, 
        error: err.response?.data?.error || 'Contraseña incorrecta'
      }
    } finally {
      setVerifyingPassword(false)
      setPendingTransferType(null)
    }
  }
  
  // Cuando el usuario CANCELA el modal de contraseña
  const handlePasswordModalClose = () => {
    setShowPasswordModal(false)
    setLoading(false)
    setLoadingMessage('')
    setPendingTransferType(null)
  }

  // Estado para el mensaje de loading durante la transferencia
  const [loadingMessage, setLoadingMessage] = useState('')

  // Ejecutar transferencia unitaria
  const executeUnitariaTransfer = async () => {
    setLoading(true)
    setLoadingMessage('Ejecutando transferencia...')
    setError('')
    setSuccess('')

    // Guardar datos del formulario antes de limpiarlo
    const savedCvuOrigen = unitariaCvuOrigen
    const savedCvuDestino = unitariaCvuDestino
    const savedMonto = cleanMontoFormat(unitariaMonto)
    const savedDescripcion = unitariaDescripcion || 'Pago de Servicios'
    const savedDestinatario = cvuDestinoInfo ? {
      nombre: cvuDestinoInfo.nombre || null,
      cuit: cvuDestinoInfo.cuit || null,
      alias: cvuDestinoInfo.alias || null
    } : null

    // Obtener account_number
    const selectedAccount = assignedAccounts?.find(acc => acc.cvu === savedCvuOrigen)
    const accountNumber = selectedAccount?.account_number || unitariaAccountNumber || null
    
    console.log('🚀 [TransferPage] Iniciando transferencia...')
    console.log('   accountNumber:', accountNumber)

    try {
      // PASO 1: Ejecutar la transferencia
      const transfers = [{
        from_cvu: savedCvuOrigen,
        amount: savedMonto,
        to_cvu: savedCvuDestino,
        concept: 'VAR',
        description: savedDescripcion,
        destinatario_nombre: savedDestinatario?.nombre || null,
        destinatario_cuit: savedDestinatario?.cuit || null
      }]

      const result = await executeBulkTransfer(transfers)
      const processId = result.data?.processId || 'N/A'
      
      console.log('✅ [TransferPage] Transferencia enviada, processId:', processId)

      // PASO 2: Obtener COELSA ID (solo si tenemos accountNumber)
      let coelsaId = null
      
      if (accountNumber) {
        setLoadingMessage('Confirmando con el banco...')
        
        // 2.1: Polling del batch status
        let attempts = 0
        const maxAttempts = 15
        let batchFinished = false
        let batchFailed = false
        let failCount = 0

        while (attempts < maxAttempts && !batchFinished) {
          console.log(`   🔄 Verificando batch status (${attempts + 1}/${maxAttempts})...`)
          try {
            const statusResponse = await getBatchStatus(processId)
            console.log('   📋 Status response:', statusResponse.data)
            
            if (statusResponse.data?.status === 'finished') {
              batchFinished = true
              
              // NUEVO: Verificar si hay transferencias fallidas
              failCount = statusResponse.data?.transaction_fail_count || 0
              const successCount = statusResponse.data?.transaction_success_count || 0
              
              console.log(`   📊 Resultados: éxito=${successCount}, fallidas=${failCount}`)
              
              if (failCount > 0) {
                batchFailed = true
                console.log('   ❌ La transferencia fue rechazada por el banco')
              } else {
                console.log('   ✅ Batch completado exitosamente!')
              }
            } else {
              await new Promise(resolve => setTimeout(resolve, 2000))
            }
          } catch (e) {
            console.log('   ⚠️ Error verificando batch:', e.message)
          }
          attempts++
        }
        
        // Si la transferencia falló, mostrar modal de error
        if (batchFailed) {
          // Mensajes aleatorios para transferencias fallidas
          const errorMessages = [
            'La transferencia no pudo completarse.',
            'No fue posible procesar la operación en este momento.',
            'La operación fue rechazada.'
          ]
          const randomMessage = errorMessages[Math.floor(Math.random() * errorMessages.length)]
          
          setTransferFailedData({
            cvuOrigen: savedCvuOrigen,
            cvuDestino: savedCvuDestino,
            monto: savedMonto,
            descripcion: savedDescripcion,
            fecha: new Date().toISOString(),
            destinatario: savedDestinatario,
            errorMessage: randomMessage
          })
          setShowTransferFailedModal(true)
          setLoading(false)
          setLoadingMessage('')
          return // Salir sin mostrar éxito
        }

        if (batchFinished) {
          setLoadingMessage('Obteniendo código COELSA...')
          
          // 2.2: Consultar movimientos
          const today = new Date().toISOString().split('T')[0]
          try {
            const movResponse = await getTransactionsCredencial({
              account: accountNumber,
              date_from: today,
              date_to: today,
              page: 1,
              forceAPI: true
            })
            
            const movements = movResponse.data || []
            console.log(`   📋 Movimientos recibidos: ${movements.length}`)
            
            // 2.3: Buscar el movimiento por monto
            const targetAmount = -Math.abs(savedMonto)
            const matchingMovement = movements.find(mov => {
              const movAmount = parseFloat(mov.amount)
              return Math.abs(movAmount - targetAmount) < 0.5
            })

            if (matchingMovement && matchingMovement.pos_date && matchingMovement.pos_time) {
              console.log('   ✅ Movimiento encontrado!')
              
              // 2.4: Obtener COELSA ID
              try {
                const infoResponse = await getTransferInfoCoelsa(
                  matchingMovement.pos_date,
                  matchingMovement.pos_time,
                  matchingMovement.autorization_code || matchingMovement.authorization_code,
                  Math.abs(parseFloat(matchingMovement.amount)).toString()
                )
                
                if (infoResponse.success && infoResponse.data?.coelsa_id) {
                  coelsaId = infoResponse.data.coelsa_id
                  console.log('   ✅ COELSA ID obtenido:', coelsaId)
                }
              } catch (e) {
                console.log('   ⚠️ Error obteniendo COELSA ID:', e.message)
              }
            }
          } catch (e) {
            console.log('   ⚠️ Error consultando movimientos:', e.message)
          }
        }
      }

      // PASO 3: Preparar datos para el modal (YA con el COELSA ID)
      setUnitariaSuccessData({
        cvuOrigen: savedCvuOrigen,
        cvuDestino: savedCvuDestino,
        monto: savedMonto,
        descripcion: savedDescripcion,
        processId: processId,
        fecha: new Date().toISOString(),
        accountNumber: accountNumber,
        coelsaId: coelsaId, // Ya viene con el COELSA ID
        destinatario: savedDestinatario
      })
      
      // PASO 4: Mostrar modal de éxito
      setShowUnitariaSuccessModal(true)
      
      // Limpiar formulario
      setUnitariaMonto('')
      setUnitariaCvuDestino('')
      setUnitariaDescripcion('Pago de Servicios')
      setCvuDestinoInfo(null)
      setUnitariaBalance(null)
      setUnitariaCvuOrigen('')
      setUnitariaAccountNumber('')

    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al ejecutar transferencia')
    } finally {
      setLoading(false)
      setLoadingMessage('')
    }
  }

  // Ejecutar transferencias después de validar contraseña
  const executeTransfersAfterValidation = async () => {
    // VALIDACIÓN: Verificar que los CVU de origen estén asignados al usuario del portal
    if (isPortalUser && assignedAccounts && assignedAccounts.length > 0) {
      const transferenciasNoAsignadas = []
      
      parsedTransfers.forEach((transfer, index) => {
        const isAssigned = assignedAccounts.some(acc => acc.cvu === transfer.from_cvu)
        if (!isAssigned) {
          transferenciasNoAsignadas.push({
            numero: index + 1,
            cvu: transfer.from_cvu
          })
        }
      })
      
      if (transferenciasNoAsignadas.length > 0) {
        // Agrupar por CVU para mostrar mensaje más claro
        const cvusUnicos = [...new Set(transferenciasNoAsignadas.map(t => t.cvu))]
        const mensajes = cvusUnicos.map(cvu => {
          const nums = transferenciasNoAsignadas.filter(t => t.cvu === cvu).map(t => t.numero)
          return `• CVU ${cvu} (transferencia${nums.length > 1 ? 's' : ''} ${nums.join(', ')})`
        })
        
        setError(
          `Los siguientes CVU de origen no los tenés asignados a tu cuenta, por favor modificalos:\n\n${mensajes.join('\n')}`
        )
        return
      }
    }

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const result = await executeBulkTransfer(parsedTransfers)
      
      const transferCount = parsedTransfers.length
      const processId = result.data?.processId || 'N/A'
      const transferWord = transferCount === 1 ? 'Transferencia enviada' : `${transferCount} transferencias enviadas`
      setSuccess(`✅ ${transferWord}\n\n📋 Process ID:\n${processId}\n\nGuarde este ID para consultar el estado del lote.`)
      
      // Limpiar después del éxito
      setUploadedFile(null)
      setParsedTransfers([])
      setShowPreview(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al ejecutar transferencias')
    } finally {
      setLoading(false)
    }
  }

  // Limpiar archivo cargado
  const handleClearFile = () => {
    setUploadedFile(null)
    setParsedTransfers([])
    setShowPreview(false)
    setError('')
    setSuccess('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount)
  }

  // Agrupar transferencias por fecha y lote (mismo minuto = mismo lote)
  const groupTransfersByDateAndLote = (transfers) => {
    if (!transfers || transfers.length === 0) return {}
    
    // Primero agrupar por día
    const byDate = {}
    
    // Opciones de formato para Argentina
    const argentinaTimeZone = 'America/Argentina/Buenos_Aires'
    
    transfers.forEach(transfer => {
      const dateStr = transfer.completed || transfer.posted || transfer.datetime || transfer.timestamp
      if (!dateStr) return
      
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return
      
      // Key para el día (usando zona horaria Argentina)
      const dayKey = date.toLocaleDateString('es-AR', {
        timeZone: argentinaTimeZone,
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      
      // Key para el lote (mismo minuto en Argentina)
      const argHour = date.toLocaleString('es-AR', { timeZone: argentinaTimeZone, hour: '2-digit', hour12: false })
      const argMinute = date.toLocaleString('es-AR', { timeZone: argentinaTimeZone, minute: '2-digit' })
      const argDate = date.toLocaleDateString('es-AR', { timeZone: argentinaTimeZone })
      const loteKey = `${argDate}-${argHour}:${argMinute}`
      
      if (!byDate[dayKey]) {
        byDate[dayKey] = {}
      }
      
      if (!byDate[dayKey][loteKey]) {
        byDate[dayKey][loteKey] = {
          timestamp: date,
          transfers: []
        }
      }
      
      byDate[dayKey][loteKey].transfers.push(transfer)
    })
    
    return byDate
  }

  // Calcular totales de un lote
  const getLoteTotals = (lote) => {
    const count = lote.transfers.length
    const total = lote.transfers.reduce((sum, t) => {
      return sum + Math.abs(t.amount || t.value?.amount || 0)
    }, 0)
    const comisionTotal = count * COMISION_COELSA
    return { count, total, comisionTotal }
  }

  // CREDENCIAL - Mostrar input para número de cuenta
  const handleShowHistoryInput = () => {
    setShowAccountInput(true)
    setHistoryAccount('')
    setDetailError('')
  }

  // CREDENCIAL - Cargar historial de transferencias (últimos 7 días)
  const handleLoadHistory = async () => {
    if (!historyAccount.trim()) {
      setDetailError('Ingrese un número de cuenta')
      return
    }

    setHistoryLoading(true)
    setHistoryTransfers([])
    setDetailError('')
    setShowAccountInput(false)
    
    try {
      // Calcular fechas (últimos 7 días)
      const now = new Date()
      const pastDays = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000))
      
      const fromDate = pastDays.toISOString().split('T')[0]
      const toDate = now.toISOString().split('T')[0]

      // Consultar movimientos con el número de cuenta ingresado
      const response = await getTransactionsCredencial({
        account: historyAccount.trim(),
        from: fromDate,
        to: toDate,
        page: 1
      })

      // Obtener todos los movimientos (el banco ya filtra por cuenta)
      const transfers = response.data || []

      setHistoryTransfers(transfers)
      setShowHistoryModal(true)
    } catch (err) {
      setDetailError('Error al cargar historial: ' + (err.response?.data?.error || err.message))
      setShowAccountInput(true)
    } finally {
      setHistoryLoading(false)
    }
  }

  // CREDENCIAL - Consultar detalle de una transferencia
  const handleViewTransferDetail = async (transfer) => {
    setSelectedTransfer(transfer)
    setTransferDetail(null)
    setDetailLoading(true)
    setDetailError('')

    try {
      // Extraer datos necesarios del movimiento
      const authCode = transfer.authorization_code || transfer.auth_code || transfer.reference
      const txDatetime = transfer.completed || transfer.posted || transfer.datetime
      const amount = Math.abs(transfer.amount || transfer.value?.amount || 0)

      if (!authCode) {
        // Sin código de autorización, no se puede consultar detalle
        setDetailLoading(false)
        return
      }

      // Formatear fecha para el endpoint
      const formattedDatetime = txDatetime ? 
        new Date(txDatetime).toISOString().replace('T', ' ').substring(0, 19) : 
        ''

      // Intentar primero con COELSA (CVU), si falla con C2C
      let result
      try {
        result = await getTransferInfoCoelsa(formattedDatetime, authCode, amount.toString())
      } catch (coelsaError) {
        // Si falla COELSA, intentar C2C
        console.log('COELSA falló, intentando C2C...')
        result = await getTransferInfoC2C(
          formattedDatetime, 
          authCode, 
          transfer.account || '',
          ''
        )
      }

      setTransferDetail(result)
    } catch (err) {
      setDetailError('Error al consultar detalle: ' + (err.response?.data?.error || err.message))
    } finally {
      setDetailLoading(false)
    }
  }

  // Cerrar modal de historial
  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false)
    setShowAccountInput(false)
    setSelectedTransfer(null)
    setTransferDetail(null)
    setDetailError('')
    setHistoryAccount('')
    setExpandedLote(null)
  }

  // Toggle expandir lote
  const toggleLote = (loteKey) => {
    setExpandedLote(expandedLote === loteKey ? null : loteKey)
    setSelectedTransfer(null)
    setTransferDetail(null)
  }

  // Calcular total
  const totalAmount = parsedTransfers.reduce((sum, t) => sum + t.amount, 0)

  const selectedAccountData = accounts.find(acc => acc.id === originAccount)

  // Esperar a que el rol esté listo Y definido
  if (!roleReady || !userRole) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando...</p>
      </div>
    )
  }

  // ============= RENDER CREDENCIAL =============
  // Solo mostrar CREDENCIAL si el rol es EXPLÍCITAMENTE 'CREDENCIAL'
  if (userRole === 'CREDENCIAL') {
    // Bloquear usuarios con rol "lector" - solo pueden consultar, no transferir
    if (isPortalUser && portalRole === 'lector') {
      return (
        <div className="page-container">
          <div className="page-header">
            <h1><LuArrowLeftRight /> Transferencias</h1>
          </div>
          <div className="access-denied-container">
            <div className="access-denied-icon">🔒</div>
            <h2>Acceso Restringido</h2>
            <p>Tu usuario tiene rol <strong>Lector</strong>, lo que significa que solo puedes consultar saldos y movimientos.</p>
            <p>Para realizar transferencias, contactá al administrador para que cambie tu rol a <strong>Principal</strong>.</p>
          </div>
        </div>
      )
    }

    // Vista de selección de tipo de transferencia
    if (transferMode === 'select') {
      return (
        <div className="page-container">
          <div className="page-header">
            <h1><LuArrowLeftRight /> Transferencias</h1>
          </div>

          <div className="transfer-mode-selector">
            <h2>Seleccioná el tipo de transferencia</h2>
            <div className="mode-buttons">
              <button 
                className="mode-button mode-unitaria"
                onClick={() => setTransferMode('unitaria')}
              >
                <LuArrowLeftRight className="mode-icon" />
                <span className="mode-title">Transferencia Unitaria</span>
                <span className="mode-desc">Realizar una sola transferencia</span>
              </button>
              <button 
                className="mode-button mode-masiva"
                onClick={() => setTransferMode('masiva')}
              >
                <LuFile className="mode-icon" />
                <span className="mode-title">Transferencias Masivas</span>
                <span className="mode-desc">Cargar múltiples transferencias desde CSV</span>
              </button>
            </div>
          </div>
        </div>
      )
    }

    // Vista de transferencia UNITARIA
    if (transferMode === 'unitaria') {
      return (
        <div className="page-container">
          <div className="page-header">
            <h1><LuArrowLeftRight /> Transferencia Unitaria</h1>
            <button className="btn-back" onClick={() => setTransferMode('select')}>
              ← Volver
            </button>
          </div>

          <div className="unitaria-form-container">
            <div className="unitaria-form">
              <div className="form-group">
                <label>CVU Origen</label>
                {isPortalUser && assignedAccounts && assignedAccounts.length > 0 ? (
                  <select
                    value={unitariaCvuOrigen}
                    onChange={(e) => handleSelectCvuOrigen(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Seleccioná un CVU</option>
                    {assignedAccounts.map((acc, idx) => (
                      <option key={idx} value={acc.cvu}>
                        {acc.account_number} - {acc.cvu}
                      </option>
                    ))}
                  </select>
                ) : (
                  <>
                  <input
                    type="text"
                    value={unitariaCvuOrigen}
                      onChange={(e) => {
                        setUnitariaCvuOrigen(e.target.value.replace(/[^0-9]/g, ''))
                        // Limpiar account_number si cambia el CVU
                        if (e.target.value.length !== 22) {
                          setUnitariaAccountNumber('')
                        }
                      }}
                    placeholder="Ingresá el CVU de origen (22 dígitos)"
                    className="form-control"
                    maxLength={22}
                  />
                    {/* Campo para account_number cuando el operador ingresa CVU manualmente */}
                    {unitariaCvuOrigen && unitariaCvuOrigen.length === 22 && (
                      <div className="account-number-input" style={{ marginTop: '10px' }}>
                        <label style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px', display: 'block' }}>
                          Número de Cuenta (requerido para obtener COELSA ID)
                        </label>
                        <input
                          type="text"
                          value={unitariaAccountNumber}
                          onChange={(e) => setUnitariaAccountNumber(e.target.value.replace(/[^0-9]/g, ''))}
                          placeholder="Ej: 564733583 (9 dígitos)"
                          className="form-control"
                          maxLength={9}
                          style={{ 
                            backgroundColor: unitariaAccountNumber.length === 9 ? '#e8f5e9' : '#fff3e0',
                            borderColor: unitariaAccountNumber.length === 9 ? '#4caf50' : '#ff9800'
                          }}
                        />
                        {unitariaAccountNumber && unitariaAccountNumber.length !== 9 && (
                          <small style={{ color: '#ff9800', fontSize: '0.75rem' }}>
                            El número de cuenta debe tener 9 dígitos
                          </small>
                        )}
                        {unitariaAccountNumber.length === 9 && (
                          <small style={{ color: '#4caf50', fontSize: '0.75rem' }}>
                            ✓ Número de cuenta válido
                          </small>
                        )}
                      </div>
                    )}
                  </>
                )}
                
                {/* Balance de la cuenta seleccionada */}
                {unitariaCvuOrigen && isPortalUser && (
                  <div className="balance-display">
                    {unitariaBalanceLoading ? (
                      <span className="balance-loading">
                        <div className="spinner-tiny"></div> Cargando balance...
                      </span>
                    ) : unitariaBalance !== null ? (
                      <span className="balance-value">
                        Balance de la cuenta: <strong>{formatCurrency(unitariaBalance)}</strong>
                      </span>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Monto</label>
                <input
                  type="text"
                  value={unitariaMonto}
                  onChange={(e) => setUnitariaMonto(formatMontoInput(e.target.value))}
                  placeholder="0"
                  className="form-control"
                  inputMode="decimal"
                />
              </div>

              {/* Campo de Alias (opcional) */}
              <div className="form-group">
                <label>Alias (opcional)</label>
                <div className="alias-input-container">
                  <input
                    type="text"
                    value={aliasInput}
                    onChange={(e) => {
                      setAliasInput(e.target.value)
                      setAliasError('')
                      setAliasVerified(false)
                    }}
                    placeholder="Escribí un alias para verificar"
                    className="form-control alias-input"
                    disabled={aliasVerifying}
                  />
                  <button
                    type="button"
                    className="btn-verify-alias"
                    onClick={handleVerifyAlias}
                    disabled={aliasVerifying || !aliasInput.trim()}
                  >
                    {aliasVerifying ? (
                      <LuLoader className="spinner-icon" />
                    ) : (
                      <LuSearch />
                    )}
                    <span>{aliasVerifying ? 'Verificando...' : 'Verificar'}</span>
                  </button>
                </div>
                {/* Feedback del alias */}
                {aliasError && (
                  <div className="alias-feedback alias-error">
                    ❌ {aliasError}
                  </div>
                )}
                {aliasVerified && !aliasError && (
                  <div className="alias-feedback alias-success">
                    ✅ Alias verificado - CVU completado automáticamente
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>CVU Destino</label>
                <input
                  type="text"
                  value={unitariaCvuDestino}
                  onChange={(e) => handleCvuDestinoChange(e.target.value)}
                  placeholder="CVU destino (22 dígitos)"
                  className="form-control"
                  maxLength={22}
                />
                
                {/* Info del CVU destino */}
                {cvuDestinoLoading && (
                  <div className="cvu-destino-info cvu-destino-loading">
                    <div className="spinner-tiny"></div>
                  </div>
                )}
                {!cvuDestinoLoading && cvuDestinoInfo && (
                  <div className="cvu-destino-info">
                    <div className="cvu-destino-data">
                      <div className="cvu-destino-row">
                        <span className="cvu-destino-label">Titular:</span>
                        <span className="cvu-destino-value">{cvuDestinoInfo.nombre || '-'}</span>
                      </div>
                      <div className="cvu-destino-row">
                        <span className="cvu-destino-label">CUIT:</span>
                        <span className="cvu-destino-value">{cvuDestinoInfo.cuit || '-'}</span>
                      </div>
                      {cvuDestinoInfo.alias && (
                        <div className="cvu-destino-row">
                          <span className="cvu-destino-label">Alias:</span>
                          <span className="cvu-destino-value">{cvuDestinoInfo.alias}</span>
                        </div>
                      )}
                      <div className="cvu-destino-row">
                        <span className="cvu-destino-label">Tipo:</span>
                        <span className="cvu-destino-value">
                          {cvuDestinoInfo.tipo_persona === 'J' ? 'Persona Jurídica' : cvuDestinoInfo.tipo_persona === 'F' ? 'Persona Física' : cvuDestinoInfo.tipo_persona || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <input
                  type="text"
                  value={unitariaDescripcion}
                  onChange={(e) => setUnitariaDescripcion(e.target.value)}
                  placeholder="Pago de Servicios"
                  className="form-control"
                  maxLength={50}
                />
              </div>

              <button 
                className="btn-action btn-execute"
                onClick={handleExecuteUnitariaTransfer}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <LuLoader className="spinner-icon" /> {loadingMessage || 'Procesando...'}
                  </>
                ) : (
                  <>
                    <LuPlay /> Ejecutar Transferencia
                  </>
                )}
              </button>

              {error && <div className="error-message">{error}</div>}
            </div>
          </div>

          <PasswordModal
            isOpen={showPasswordModal}
            onClose={handlePasswordModalClose}
            onConfirm={handlePasswordConfirm}
            title="Confirmar Transferencia"
            message="Por seguridad, ingresá tu contraseña para ejecutar la transferencia."
            loading={verifyingPassword}
          />

          {/* Modal de éxito con descarga de comprobante */}
          <TransferSuccessModal
            isOpen={showUnitariaSuccessModal}
            onClose={() => {
              setShowUnitariaSuccessModal(false)
              setUnitariaSuccessData(null)
            }}
            transferData={unitariaSuccessData}
          />

          {/* Modal de TRANSFERENCIA FALLIDA */}
          {showTransferFailedModal && transferFailedData && (
            <div className="transfer-failed-overlay">
              <div className="transfer-failed-modal">
                <div className="transfer-failed-header">
                  <div className="failed-icon">❌</div>
                  <h2>Transferencia Fallida</h2>
                </div>
                
                <div className="transfer-failed-body">
                  <p className="failed-message">
                    {transferFailedData.errorMessage}
                  </p>
                  <p className="failed-submessage">
                    No se descontó dinero de tu cuenta.
                  </p>
                  
                  <div className="failed-details">
                    <div className="failed-detail-row">
                      <span className="label">Monto:</span>
                      <span className="value">${transferFailedData.monto?.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="failed-detail-row">
                      <span className="label">CVU Destino:</span>
                      <span className="value">{transferFailedData.cvuDestino}</span>
                    </div>
                    {transferFailedData.destinatario?.nombre && (
                      <div className="failed-detail-row">
                        <span className="label">Destinatario:</span>
                        <span className="value">{transferFailedData.destinatario.nombre}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="transfer-failed-footer">
                  <button 
                    className="btn-failed-close"
                    onClick={() => {
                      setShowTransferFailedModal(false)
                      setTransferFailedData(null)
                    }}
                  >
                    Entendido
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )
    }

    // Vista de transferencias MASIVAS (modo existente)
    return (
      <div className="page-container">
        <div className="page-header">
          <h1><LuArrowLeftRight /> Transferencias Masivas</h1>
          <button className="btn-back" onClick={() => setTransferMode('select')}>
            ← Volver
          </button>
        </div>

        <div className="transfer-layout">
          {/* Columna Izquierda - Acciones */}
          <div className="transfer-actions-panel">
            <h3>Acciones</h3>
            
            {/* Botón Descargar Plantilla */}
            <button 
              className="btn-action btn-download"
              onClick={handleDownloadTemplate}
            >
              <LuDownload /> Descargar Plantilla
            </button>

            {/* Botón Cargar Plantilla */}
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <button 
              className="btn-action btn-upload"
              onClick={() => fileInputRef.current?.click()}
            >
              <LuUpload /> Cargar Plantilla
            </button>

            {/* Archivo cargado */}
            {uploadedFile && (
              <div className="uploaded-file-info">
                <LuFile className="file-icon" />
                <div className="file-details">
                  <span className="file-name">{uploadedFile.name}</span>
                  <span className="file-count">{parsedTransfers.length} transferencia(s)</span>
                  <span className="file-total">Total: {formatCurrency(totalAmount)}</span>
                </div>
                <button className="btn-clear" onClick={handleClearFile}>
                  <LuTrash2 />
                </button>
              </div>
            )}

            {/* Botón Ejecutar */}
            {uploadedFile && parsedTransfers.length > 0 && (
              <button 
                className="btn-action btn-execute"
                onClick={handleExecuteBulkTransfer}
                disabled={loading}
              >
                <LuPlay /> {loading ? 'Procesando...' : 'Ejecutar'}
              </button>
            )}

            {/* Mensajes */}
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message" style={{ whiteSpace: 'pre-line' }}>{success}</div>}

            {/* Preview de transferencias */}
            {showPreview && parsedTransfers.length > 0 && (
              <div className="preview-section">
                <h4>Vista Previa</h4>
                <div className="preview-summary">
                  <span><strong>Cantidad:</strong> {parsedTransfers.length} transferencias</span>
                  <span><strong>Total:</strong> {formatCurrency(totalAmount)}</span>
                </div>
                <div className="preview-table-container">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>CVU Origen</th>
                        <th>Monto</th>
                        <th>CVU Destino</th>
                        <th>Concepto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedTransfers.slice(0, 10).map((t, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td className="cvu-cell">{t.from_cvu.substring(0, 10)}...</td>
                          <td>{formatCurrency(t.amount)}</td>
                          <td className="cvu-cell">{t.to_cvu.substring(0, 10)}...</td>
                          <td>{t.concept}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsedTransfers.length > 10 && (
                    <p className="preview-more">... y {parsedTransfers.length - 10} más</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Columna Derecha - Instrucciones */}
          <div className="transfer-instructions-panel">
            <h3>Cómo realizar transferencias masivas</h3>
            
            <ol className="instructions-list">
              <li>
                <LuDownload className="step-icon" />
                <span>Presionar en <strong>Descargar Plantilla</strong></span>
              </li>
              <li>
                <LuFile className="step-icon" />
                <span>Completar la plantilla con la información de las transferencias</span>
              </li>
              <li>
                <LuUpload className="step-icon" />
                <span>Hacer click en <strong>Cargar Plantilla</strong></span>
              </li>
              <li>
                <LuCircleCheck className="step-icon" />
                <span>Verificar que aparezca la plantilla cargada con la cantidad de transferencias</span>
              </li>
              <li>
                <LuPlay className="step-icon" />
                <span>Presionar en <strong>Ejecutar</strong></span>
              </li>
            </ol>

            <div className="instructions-note">
              <h4>Formato de la plantilla CSV:</h4>
              <table className="format-table">
                <thead>
                  <tr>
                    <th>CVU Origen</th>
                    <th>Monto</th>
                    <th>CVU Destino</th>
                    <th>Concepto</th>
                    <th>Descripción</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>0000003100...</td>
                    <td>1500.50</td>
                    <td>0000003100...</td>
                    <td>VAR</td>
                    <td>Pago servicios</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Modal de Funcionalidad en Desarrollo */}
        {showDevModal && (
          <div className="modal-overlay" onClick={() => setShowDevModal(false)}>
            <div className="dev-modal" onClick={(e) => e.stopPropagation()}>
              <div className="dev-modal-content">
                <LuHistory className="dev-modal-icon" />
                <h2>Funcionalidad temporalmente inhabilitada</h2>
                <p>En desarrollo</p>
                <button 
                  className="btn-primary"
                  onClick={() => setShowDevModal(false)}
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Historial de Transferencias (deshabilitado) */}
        {showHistoryModal && false && (
          <div className="modal-overlay" onClick={handleCloseHistoryModal}>
            <div className="history-modal" onClick={(e) => e.stopPropagation()}>
              <div className="history-modal-header">
                <h2><LuHistory /> Estado de Transferencias (Últimos 7 días)</h2>
                <button className="btn-close" onClick={handleCloseHistoryModal}>
                  <LuX />
                </button>
              </div>

              <div className="history-modal-body">
                {historyLoading ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Cargando transferencias...</p>
                  </div>
                ) : historyTransfers.length === 0 ? (
                  <div className="empty-history">
                    <p>No se encontraron transferencias en los últimos 7 días.</p>
                    <p className="hint">Las transferencias pueden tardar unos minutos en aparecer.</p>
                  </div>
                ) : (
                  <div className={`history-content ${selectedTransfer ? 'with-detail' : ''}`}>
                    {/* Lista de lotes agrupados por fecha */}
                    <div className="history-list">
                      {Object.entries(groupTransfersByDateAndLote(historyTransfers)).map(([dateKey, lotes]) => (
                        <div key={dateKey} className="date-group">
                          <h4 className="date-header">{dateKey}</h4>
                          
                          {Object.entries(lotes).map(([loteKey, lote], loteIdx) => {
                            const { count, total, comisionTotal } = getLoteTotals(lote)
                            const isExpanded = expandedLote === loteKey
                            const loteTime = lote.timestamp.toLocaleTimeString('es-AR', { 
                              timeZone: 'America/Argentina/Buenos_Aires',
                              hour: '2-digit', 
                              minute: '2-digit',
                              hour12: false 
                            }) + 'hs'
                            
                            return (
                              <div key={loteKey} className="lote-container">
                                <div 
                                  className={`lote-header ${isExpanded ? 'expanded' : ''}`}
                                  onClick={() => toggleLote(loteKey)}
                                >
                                  <div className="lote-info">
                                    <LuFile className="lote-icon" />
                                    <span className="lote-name">Lote {loteIdx + 1} - {loteTime}</span>
                                    <span className="lote-count">({count} transferencias)</span>
                                  </div>
                                  <div className="lote-total">
                                    {formatCurrency(total)}
                                  </div>
                                </div>
                                
                                {isExpanded && (
                                  <div className="lote-detail">
                                    <div className="lote-transfers">
                                      {lote.transfers.map((transfer, idx) => (
                                        <div key={idx} className="transfer-row">
                                          <span className="transfer-idx">{idx + 1}.</span>
                                          <span className="transfer-desc">
                                            {transfer.description || transfer.merchant_name || transfer.tx_description || '-'}
                                          </span>
                                          <span className="transfer-amount">
                                            {formatCurrency(Math.abs(transfer.amount || transfer.value?.amount || 0))}
                                          </span>
                                          <button 
                                            className="btn-view-detail"
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleViewTransferDetail(transfer)
                                            }}
                                          >
                                            <LuEye />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                    
                                    <div className="lote-summary">
                                      <div className="summary-row">
                                        <span>Comisión por transferencia:</span>
                                        <span>{formatCurrency(COMISION_COELSA)}</span>
                                      </div>
                                      <div className="summary-row">
                                        <span>Total comisiones ({count} x {formatCurrency(COMISION_COELSA)}):</span>
                                        <span className="comision-total">{formatCurrency(comisionTotal)}</span>
                                      </div>
                                      <div className="summary-row total">
                                        <span>Monto total transferido:</span>
                                        <span className="monto-total">{formatCurrency(total)}</span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      ))}
                    </div>

                    {/* Detalle de transferencia seleccionada */}
                    {selectedTransfer && (
                      <div className="transfer-detail-panel">
                        <h4>Detalle de Transferencia</h4>
                        
                        {detailLoading ? (
                          <div className="loading-container">
                            <div className="spinner-small"></div>
                            <p>Consultando detalle...</p>
                          </div>
                        ) : detailError ? (
                          <div className="error-message">{detailError}</div>
                        ) : transferDetail ? (
                          <div className="detail-content">
                            {transferDetail.data?.status && (
                              <div className={`status-badge status-${transferDetail.data.status.toLowerCase()}`}>
                                {transferDetail.data.status}
                              </div>
                            )}
                            
                            <div className="detail-grid">
                              {transferDetail.data?.from && (
                                <div className="detail-section">
                                  <h5>Origen</h5>
                                  <p><strong>Nombre:</strong> {transferDetail.data.from.card_holder_name || transferDetail.data.sender_name || '-'}</p>
                                  <p><strong>CVU:</strong> {transferDetail.data.from.cvu || transferDetail.data.sender_account || '-'}</p>
                                  <p><strong>CUIT:</strong> {transferDetail.data.from.persona_tax_id || transferDetail.data.sender_tax_id || '-'}</p>
                                </div>
                              )}
                              
                              {transferDetail.data?.to && (
                                <div className="detail-section">
                                  <h5>Destino</h5>
                                  <p><strong>Nombre:</strong> {transferDetail.data.to.card_holder_name || transferDetail.data.receiver_name || '-'}</p>
                                  <p><strong>CVU:</strong> {transferDetail.data.to.cvu || transferDetail.data.receiver_account || '-'}</p>
                                  <p><strong>CUIT:</strong> {transferDetail.data.to.persona_tax_id || transferDetail.data.receiver_tax_id || '-'}</p>
                                </div>
                              )}

                              {!transferDetail.data?.from && !transferDetail.data?.to && (
                                <div className="detail-section full-width">
                                  <p><strong>Monto:</strong> {formatCurrency(transferDetail.data?.amount || selectedTransfer.amount)}</p>
                                  <p><strong>Concepto:</strong> {transferDetail.data?.concept || '-'}</p>
                                  <p><strong>Código:</strong> {transferDetail.code || '-'}</p>
                                  <p><strong>Descripción:</strong> {transferDetail.description || '-'}</p>
                                </div>
                              )}
                            </div>

                            {transferDetail.data?.transfer_timestamp && (
                              <p className="detail-timestamp">
                                <strong>Fecha:</strong> {new Date(transferDetail.data.transfer_timestamp).toLocaleString('es-AR')}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="hint">Haz clic en una transferencia para ver su detalle.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ============= RENDER BIND =============
  const handleBindTransfer = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setLoading(true)

    try {
      // Construir objeto de destino según el tipo
      const to = {}
      if (destinationType === 'alias') {
        to.label = destinationValue
      } else if (destinationType === 'cbu') {
        to.cbu = destinationValue
      } else if (destinationType === 'cuit') {
        to.cuit = destinationValue
      }

      const transferAmount = parseFloat(amount)
      const resolvedAccountId = originAccount.startsWith('registered-cvu:')
        ? registeredCVUs.find(c => c.cvu === originAccount.replace('registered-cvu:', ''))?.account_id || originAccount
        : originAccount
      const transferData = {
        origin_id: generateOriginId(),
        origin_account_id: resolvedAccountId,
        to,
        value: {
          currency: 'ARS',
          amount: transferAmount
        },
        description: description || 'Transferencia',
        concept: concept || 'VAR',
        emails: email ? [email] : []
      }

      const result = await executeTransfer(transferData)
      
      // Obtener datos de la cuenta origen para mostrar en el modal
      const originAccountData = accounts.find(acc => acc.id === resolvedAccountId)
      const originDisplay = originAccount.startsWith('registered-cvu:')
        ? originAccount.replace('registered-cvu:', '')
        : (originAccountData?.label || originAccountData?.account_routing?.address || originAccount)
      
      // Guardar datos para el modal de éxito
      setSuccessTransferData({
        origen: originDisplay,
        destino: destinationValue,
        tipoDestino: destinationType,
        monto: transferAmount
      })
      setShowSuccessModal(true)
      
      // Limpiar formulario
      setDestinationValue('')
      setAmount('')
      setDescription('Transferencia')
      setEmail('')
    } catch (err) {
      setError(err.response?.data?.error || 'Error al realizar la transferencia')
    } finally {
      setLoading(false)
    }
  }

  // Cerrar modal de éxito
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false)
    setSuccessTransferData(null)
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><LuArrowLeftRight /> Transferencias</h1>
      </div>

      <form onSubmit={handleBindTransfer} className="transfer-form">
        <div className="form-section">
          <h3>Cuenta Origen</h3>
          <div className="form-group">
            <label>Cuenta</label>
            <select
              value={originAccount}
              onChange={(e) => setOriginAccount(e.target.value)}
              className="form-control"
              required
            >
              <option value="">Seleccione una cuenta</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.number}{acc.type ? ` - ${acc.type}` : ''}{acc.balance?.amount ? ` - ${formatCurrency(acc.balance.amount)}` : ''}
                </option>
              ))}
              {registeredCVUs.length > 0 && (
                <optgroup label="CVUs dados de alta">
                  {registeredCVUs.map((cvu, idx) => (
                    <option key={`cvu-${idx}`} value={`registered-cvu:${cvu.cvu}`}>
                      {cvu.cvu}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Destino</h3>
          <div className="form-group">
            <label>Tipo de destino</label>
            <select
              value={destinationType}
              onChange={(e) => {
                setDestinationType(e.target.value)
                setDestinationValue('')
              }}
              className="form-control"
            >
              <option value="alias">Alias CBU</option>
              <option value="cbu">CBU/CVU</option>
              <option value="cuit">CUIT</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              {destinationType === 'alias' && 'Alias CBU'}
              {destinationType === 'cbu' && 'CBU/CVU'}
              {destinationType === 'cuit' && 'CUIT'}
            </label>
            <input
              type="text"
              value={destinationValue}
              onChange={(e) => setDestinationValue(e.target.value)}
              className="form-control"
              placeholder={
                destinationType === 'alias' ? 'ej: MI.ALIAS.CBU' :
                destinationType === 'cbu' ? 'ej: 0000000000000000000000' :
                'ej: 20123456789'
              }
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Detalles de la Transferencia</h3>
          
          <div className="form-group">
            <label>Monto (ARS)</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="form-control"
              placeholder="0.00"
              min="0.01"
              step="0.01"
              required
            />
          </div>

          <div className="form-group">
            <label>Concepto</label>
            <select
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
              className="form-control"
            >
              <option value="VAR">Varios</option>
              <option value="ALQ">Alquiler</option>
              <option value="CUO">Cuota</option>
              <option value="EXP">Expensas</option>
              <option value="FAC">Factura</option>
              <option value="PRE">Préstamo</option>
              <option value="SEG">Seguro</option>
              <option value="HON">Honorarios</option>
              <option value="HAB">Haberes</option>
            </select>
          </div>

          <div className="form-group">
            <label>Descripción</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-control"
              placeholder="Descripción de la transferencia"
              maxLength="100"
            />
          </div>

          <div className="form-group">
            <label>Email para comprobante (opcional)</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-control"
              placeholder="email@ejemplo.com"
            />
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-actions">
          <button
            type="button"
            onClick={() => {
              setDestinationValue('')
              setAmount('')
              setDescription('Transferencia')
              setEmail('')
              setError('')
              setSuccess('')
            }}
            className="btn-secondary"
            disabled={loading}
          >
            Limpiar
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !originAccount || !destinationValue || !amount}
          >
            <LuArrowLeftRight /> {loading ? 'Procesando...' : 'Transferir'}
          </button>
        </div>
      </form>

      {/* Modal de éxito */}
      {showSuccessModal && successTransferData && (
        <div className="modal-overlay" onClick={handleCloseSuccessModal}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-modal-icon">
              <LuCircleCheck />
            </div>
            <h2>¡Transferencia enviada!</h2>
            
            <div className="success-modal-details">
              <div className="detail-row">
                <span className="detail-label">Origen:</span>
                <span className="detail-value">{successTransferData.origen}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">
                  Destino ({successTransferData.tipoDestino === 'alias' ? 'Alias' : successTransferData.tipoDestino === 'cbu' ? 'CBU/CVU' : 'CUIT'}):
                </span>
                <span className="detail-value">{successTransferData.destino}</span>
              </div>
              <div className="detail-row detail-amount">
                <span className="detail-label">Monto:</span>
                <span className="detail-value">{formatCurrency(successTransferData.monto)}</span>
              </div>
            </div>

            <button className="btn-primary" onClick={handleCloseSuccessModal}>
              Aceptar
            </button>
          </div>
        </div>
      )}

      {/* Modal de confirmación de contraseña para transferencias */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={handlePasswordModalClose}
        onConfirm={handlePasswordConfirm}
        title="Confirmar Transferencia"
        message="Por seguridad, ingresá tu contraseña para ejecutar las transferencias."
        loading={verifyingPassword}
      />
    </div>
  )
}

export default TransferPage
